(() => {
  const configElement = document.getElementById('bleusalt-favorites-config');
  if (!configElement || window.bleusaltFavorites) return;
  const config = JSON.parse(configElement.textContent);
  const strings = config.strings;
  const rawId = (id) => String(id || '').split('/').pop();
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  const storageKey = 'bleusalt:favorites:v1:guest';
  const transferKey = '_bleusalt_favorite_transfer';
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const same = (a, b) => a.variantId === b.variantId && JSON.stringify(a.properties) === JSON.stringify(b.properties);
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  };

  class Favorites {
    constructor() {
      this.items = [];
      this.products = new Map();
      this.pendingProducts = new Map();
      this.selections = new Map();
      this.busy = false;
      this.loaded = false;
      this.error = '';
      this.revision = 0;
      this.loadPromise = null;
      document.addEventListener('click', (event) => this.onClick(event));
      document.addEventListener('change', (event) => {
        const select = event.target.closest('[data-favorites-variant]');
        if (!select) return;
        event.stopPropagation();
        this.selections.set(select.dataset.favoritesVariant, select.value);
        this.render();
      }, true);
      window.addEventListener('storage', (event) => {
        if (!config.customer && event.key === storageKey) {
          try { this.items = this.read(); this.revision++; this.render(); } catch (error) { this.report(error); }
        }
      });
      new MutationObserver((mutations) => {
        if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((el) => el.nodeType === 1 && (el.matches('[data-favorites-panel],[data-favorites-toggle]') || el.querySelector('[data-favorites-panel],[data-favorites-toggle]'))))) this.render();
      }).observe(document.body, { childList: true, subtree: true });
      const drawer = document.querySelector('cart-drawer');
      if (drawer) new MutationObserver(() => { this.render(); this.hydrate(); }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this.load().catch((error) => this.report(error));
    }

    async request(url, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {
          method: body === undefined ? 'GET' : 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400 || data.error || data.errors) throw new Error(strings.error);
        return data;
      } finally { clearTimeout(timer); }
    }

    read() {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (!Array.isArray(saved)) throw new Error();
        return saved.filter((item) => item && typeof item.id === 'string' && /^\d+$/.test(String(item.variantId)) && item.properties && typeof item.properties === 'object');
      } catch { throw new Error(strings.storage_error); }
    }

    write(items) {
      try { localStorage.setItem(storageKey, JSON.stringify(items)); }
      catch { throw new Error(strings.storage_error); }
      this.items = items;
    }

    normalize(item) {
      const product = item.product;
      return {
        id: item.id,
        variantId: rawId(item.variantId || product?.variant?.id),
        productId: rawId(item.productId || product?.id),
        handle: product?.handle || item.properties?.bleusalt?.handle || item.handle || '',
        title: product?.title || item.title || strings.unavailable,
        image: product?.variant?.image?.url || item.image || '',
        quantity: item.quantity || 1,
        properties: item.properties || {},
        missing: !product,
      };
    }

    async load() {
      if (this.loadPromise) return this.loadPromise;
      this.loadPromise = (async () => {
        this.error = '';
        this.render();
        if (config.customer) {
          const data = await this.request(config.proxy);
          if (!Array.isArray(data.items)) throw new Error(strings.error);
          this.items = data.items.map((item) => this.normalize(item));
          let guests = [];
          try { guests = this.read(); } catch { guests = []; }
          for (const item of guests) {
            await this.save(item);
            try {
              const remaining = this.read().filter((saved) => saved.id !== item.id);
              localStorage.setItem(storageKey, JSON.stringify(remaining));
            } catch { break; }
          }
        } else { this.items = this.read(); }
        this.loaded = true;
        this.revision++;
        this.render();
        this.hydrate();
      })().catch((error) => { this.loaded = false; throw error; }).finally(() => { this.loadPromise = null; });
      return this.loadPromise;
    }

    async save(item) {
      const existing = this.items.find((saved) => same(saved, item));
      if (existing) return existing;
      let saved = item;
      if (config.customer) {
        const response = await this.request(config.proxy, { action: 'add', variantId: item.variantId, quantity: item.quantity, properties: item.properties });
        if (!response.itemId) throw new Error(strings.error);
        saved = { ...item, id: response.itemId };
        this.items = [...this.items.filter((entry) => entry.id !== saved.id), saved];
      } else {
        const latest = this.read();
        const match = latest.find((entry) => same(entry, item));
        if (match) { this.items = latest; return match; }
        this.write([...latest, saved]);
      }
      this.revision++;
      return saved;
    }

    async remove(item) {
      if (config.customer) {
        await this.request(config.proxy, { action: 'remove', itemId: item.id });
        this.items = this.items.filter((entry) => entry.id !== item.id);
      } else { this.write(this.read().filter((entry) => entry.id !== item.id)); }
      this.selections.delete(item.id);
      this.revision++;
    }

    async run(operation) {
      if (this.busy) return;
      this.busy = true;
      this.error = '';
      this.render();
      try {
        if (!this.loaded) await this.load();
        const message = await operation();
        this.announce(message);
      } catch (error) { this.report(error); }
      finally { this.busy = false; this.render(); this.hydrate(); }
    }

    report(error) {
      this.error = error?.message === strings.storage_error || error?.message === strings.cart_error ? error.message : strings.error;
      this.announce(this.error);
      this.render();
    }

    announce(message) {
      const status = document.querySelector('[data-favorites-status]');
      if (status) {
        clearTimeout(this.toastTimer);
        status.hidden = !message;
        status.textContent = message || '';
        this.toastTimer = setTimeout(() => { status.hidden = true; }, 6000);
      }
    }

    async onClick(event) {
      const opener = event.target.closest('[data-favorites-open]');
      if (opener && document.querySelector('cart-drawer')) {
        event.preventDefault();
        document.querySelector('header-drawer')?.closeMenuDrawer(event);
        const drawer = document.querySelector('cart-drawer');
        drawer.open(opener);
        if (!this.loaded) this.load().catch((error) => this.report(error));
        setTimeout(() => {
          const heading = drawer.querySelector('[data-favorites-heading]');
          heading?.focus({ preventScroll: true });
          heading?.scrollIntoView({ block: 'start', behavior: 'smooth' });
          this.hydrate();
        }, 350);
        return;
      }
      const heart = event.target.closest('[data-favorites-toggle]');
      const move = event.target.closest('[data-favorites-move]');
      const action = event.target.closest('[data-favorites-action]');
      if (!heart && !move && !action) return;
      event.preventDefault();
      event.stopPropagation();
      if (action?.dataset.favoritesAction === 'retry') {
        this.loaded = false;
        this.pendingProducts.clear();
        this.products.clear();
        this.run(async () => '');
      } else if (heart) {
        this.run(async () => {
          const matches = this.items.filter((item) => item.productId === heart.dataset.productId);
          if (matches.length) {
            for (const item of matches) await this.remove(item);
            return strings.removed;
          }
          const card = heart.closest('.card,[data-favorites-card]');
          const href = card?.querySelector('a[data-product-url],a[href*="/products/"]')?.href;
          const selected = href ? new URL(href, location.origin).searchParams.get('variant') : null;
          await this.save({
            id: crypto.randomUUID(), productId: heart.dataset.productId, variantId: selected || heart.dataset.variantId,
            handle: heart.dataset.handle, title: heart.dataset.title,
            image: card?.querySelector('.card__media img,img')?.src || heart.dataset.image,
            quantity: 1, properties: { bleusalt: { needsOptions: heart.dataset.needsOptions === 'true', handle: heart.dataset.handle } },
          });
          return strings.saved;
        });
      } else if (move) {
        this.run(() => this.moveFromCart(move.dataset.lineKey));
      } else {
        this.run(async () => {
          const item = this.items.find((entry) => entry.id === action.dataset.itemId);
          if (!item) return '';
          if (action.dataset.favoritesAction === 'remove') { await this.remove(item); return strings.removed; }
          return this.moveToCart(item);
        });
      }
    }

    async moveFromCart(key) {
      const cart = await this.request(`${root}cart.js`);
      const primary = cart.items.find((item) => item.key === key);
      if (!primary) throw new Error(strings.error);
      const props = primary.properties || {};
      const linked = cart.items.filter((item) => item.key === key ||
        (props._bundle && props.bundle_id && item.properties?.bundle_id === props.bundle_id) ||
        (props._monogram_id && item.properties?._monogram_id === props._monogram_id) ||
        (props._boldBuilderId && item.properties?._boldBuilderId === props._boldBuilderId));
      const cartItems = linked.map((item) => {
        const properties = { ...item.properties };
        delete properties[transferKey];
        return { id: item.variant_id, quantity: item.quantity, properties, ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}) };
      });
      const handle = primary.handle || new URL(primary.url, location.origin).pathname.split('/products/')[1]?.split('/')[0] || '';
      await this.save({ id: crypto.randomUUID(), variantId: String(primary.variant_id), productId: String(primary.product_id), title: primary.product_title, handle, image: primary.image, quantity: primary.quantity, properties: { bleusalt: { cartItems, needsOptions: false, handle } } });
      try {
        const current = await this.request(`${root}cart.js`);
        if (linked.some((old) => !current.items.some((line) => line.key === old.key && line.quantity === old.quantity))) throw new Error();
        const result = await this.request(`${root}cart/update.js`, { updates: Object.fromEntries(linked.map((item) => [item.key, 0])), sections: 'cart-drawer,cart-icon-bubble', sections_url: location.pathname });
        if (result.items.some((item) => linked.some((old) => old.key === item.key))) throw new Error();
        await this.refreshCart(result);
      } catch { throw new Error(strings.cart_error); }
      return strings.moved_to_favorites;
    }

    async moveToCart(item) {
      const selected = this.selections.get(item.id);
      if (item.properties.bleusalt?.needsOptions && !selected) throw new Error(strings.error);
      const savedItems = item.properties.bleusalt?.cartItems;
      const items = savedItems?.length ? clone(savedItems) : [{ id: selected || item.variantId, quantity: item.quantity, properties: {} }];
      items.forEach((entry, index) => {
        entry.properties = { ...entry.properties, [transferKey]: `${item.id}:${index}` };
      });
      try {
        const before = await this.request(`${root}cart.js`);
        const missing = items.map((entry) => {
          const existing = before.items.find((line) => line.properties?.[transferKey] === entry.properties[transferKey] && String(line.variant_id) === String(entry.id));
          return { ...entry, quantity: Math.max(0, entry.quantity - (existing?.quantity || 0)) };
        }).filter((entry) => entry.quantity > 0);
        if (missing.length) await this.request(`${root}cart/add.js`, { items: missing, sections: 'cart-drawer,cart-icon-bubble', sections_url: location.pathname });
        const after = await this.request(`${root}cart.js`);
        if (!items.every((entry) => after.items.some((line) => line.properties?.[transferKey] === entry.properties[transferKey] && String(line.variant_id) === String(entry.id) && line.quantity >= entry.quantity))) throw new Error();
        await this.refreshCart(after);
        await this.remove(item);
      } catch { throw new Error(strings.cart_error); }
      return strings.moved_to_cart;
    }

    async refreshCart(cart) {
      let sections = cart.sections;
      if (!sections?.['cart-drawer'] || !sections?.['cart-icon-bubble']) sections = await this.request(`${root}?sections=cart-drawer,cart-icon-bubble`);
      if (!sections?.['cart-drawer'] || !sections?.['cart-icon-bubble']) throw new Error(strings.cart_error);
      const drawer = document.querySelector('cart-drawer');
      for (const [id, selector] of [['cart-drawer', 'cart-drawer .drawer__inner'], ['cart-icon-bubble', '#cart-icon-bubble']]) {
        const html = new DOMParser().parseFromString(sections[id] || '', 'text/html');
        const target = document.querySelector(selector);
        const source = html.querySelector(id === 'cart-drawer' ? '.drawer__inner' : '.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      }
      drawer?.classList.toggle('is-empty', cart.item_count === 0);
      drawer?.wearWith?.();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      this.render();
      drawer?.querySelector('[data-favorites-heading]')?.focus({ preventScroll: true });
    }

    hydrate() {
      if (!document.querySelector('cart-drawer.active')) return;
      for (const item of this.items) {
        if (!item.handle || this.products.has(item.handle) || this.pendingProducts.has(item.handle)) continue;
        if ((this.pendingProductCount || 0) >= 4) break;
        this.pendingProductCount = (this.pendingProductCount || 0) + 1;
        const promise = this.request(`${root}products/${encodeURIComponent(item.handle)}.js`).then((product) => {
          this.products.set(item.handle, product);
          this.revision++;
          this.render();
        }).catch(() => { this.products.set(item.handle, null); this.error = strings.error; this.revision++; this.render(); }).finally(() => { this.pendingProductCount--; this.hydrate(); });
        this.pendingProducts.set(item.handle, promise);
      }
    }

    render() {
      document.querySelectorAll('[data-favorites-toggle]').forEach((button) => {
        const saved = this.items.some((item) => item.productId === button.dataset.productId);
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', (saved ? strings.remove : strings.add).replace('__title__', button.dataset.title));
        button.disabled = this.busy;
      });
      document.querySelectorAll('[data-favorites-move]').forEach((button) => { button.disabled = this.busy; });
      document.querySelectorAll('cart-drawer .cart-items').forEach((table) => { table.inert = this.busy; });
      document.querySelectorAll('[data-favorites-menu-count]').forEach((el) => { el.textContent = this.items.length ? `(${this.items.length})` : ''; });
      document.querySelectorAll('[data-favorites-panel]').forEach((panel) => {
        const stamp = JSON.stringify([this.revision, this.busy, this.loaded, this.error, Array.from(this.selections)]);
        if (panel.dataset.stamp === stamp) return;
        panel.dataset.stamp = stamp;
        panel.querySelector('[data-favorites-count]').textContent = this.items.length ? `(${this.items.length})` : '';
        const status = panel.querySelector('[data-favorites-panel-status]');
        status.replaceChildren(document.createTextNode(this.error || (!this.loaded ? strings.loading : (!this.items.length ? strings.empty : ''))));
        if (this.error) {
          const retry = node('button', 'bleusalt-favorites__retry', strings.retry);
          retry.type = 'button'; retry.dataset.favoritesAction = 'retry'; retry.disabled = this.busy; status.append(' ', retry);
        }
        const list = panel.querySelector('[data-favorites-items]');
        list.replaceChildren(...this.items.map((item) => this.row(item)));
      });
    }

    row(item) {
      const row = node('article', 'bleusalt-favorites__row');
      const image = node('img', 'bleusalt-favorites__image');
      image.alt = item.title; image.width = 80; image.height = 106; image.loading = 'lazy';
      if (item.image && /^(https?:)?\/\//.test(item.image)) image.src = item.image;
      const copy = node('div');
      const title = node(item.handle ? 'a' : 'span', 'bleusalt-favorites__title', item.title);
      if (item.handle) title.href = `${root}products/${encodeURIComponent(item.handle)}?variant=${encodeURIComponent(item.variantId)}`;
      copy.append(title);
      const product = this.products.get(item.handle);
      const variants = product?.variants || [];
      const needsOptions = item.properties.bleusalt?.needsOptions;
      const selected = this.selections.get(item.id) || (!needsOptions ? item.variantId : '');
      const variant = variants.find((entry) => String(entry.id) === selected);
      if (variant) copy.append(node('p', 'bleusalt-favorites__price', new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency || window.Shopify?.currency?.active || 'USD' }).format(variant.price / 100)));
      if (item.quantity > 1) copy.append(node('p', 'bleusalt-favorites__detail', `${strings.quantity}: ${item.quantity}`));
      if (needsOptions && variants.length) {
        const label = node('label', 'visually-hidden', `${strings.options}: ${item.title}`);
        const select = node('select', 'bleusalt-favorites__select');
        select.id = `Favorites-variant-${item.id}`; label.htmlFor = select.id;
        select.dataset.favoritesVariant = item.id;
        select.disabled = this.busy;
        const placeholder = node('option', '', strings.choose_options);
        placeholder.value = '';
        select.append(placeholder);
        variants.forEach((entry) => {
          const option = node('option', '', `${entry.title}${entry.available ? '' : ` — ${strings.unavailable}`}`);
          option.value = String(entry.id);
          option.disabled = !entry.available; select.append(option);
        });
        select.value = selected;
        copy.append(label, select);
      } else if (variant && variant.title !== 'Default Title') { copy.append(node('p', 'bleusalt-favorites__detail', variant.title)); }
      const unavailable = this.products.has(item.handle) && (!product || (!needsOptions && !variant?.available) || (needsOptions && !variants.some((entry) => entry.available)));
      if (unavailable) copy.append(node('p', 'bleusalt-favorites__detail', strings.unavailable));
      const actions = node('div', 'bleusalt-favorites__actions');
      const move = node('button', '', strings.move_to_cart);
      move.type = 'button'; move.dataset.favoritesAction = 'cart'; move.dataset.itemId = item.id;
      move.disabled = this.busy || !product || !variant?.available || unavailable;
      const remove = node('button', '', strings.remove_item);
      remove.setAttribute('aria-label', strings.remove.replace('__title__', item.title));
      remove.type = 'button'; remove.dataset.favoritesAction = 'remove'; remove.dataset.itemId = item.id; remove.disabled = this.busy;
      actions.append(move, remove); copy.append(actions); row.append(image, copy);
      return row;
    }
  }
  window.bleusaltFavorites = new Favorites();
})();
